import DateInput from '../../components/DateInput';
/**
 * PatientOutcomes30Day — v1.1.
 * Preview + send the 30-day outcomes report to the patient's referring GP.
 * Also lets staff capture / update the GP referrer details.
 *
 * Mounted at /admin/patients/:patient_id/outcomes-30day.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { ChevronLeft, FileDown, Mail, Save, Loader2, AlertCircle, TrendingDown, Activity, Target, CheckCircle2, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import { safeConfirm } from '../../lib/confirm';

export default function PatientOutcomes30Day() {
  const { patient_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingGp, setSavingGp] = useState(false);
  const [gp, setGp] = useState({ name: '', email: '', phone: '', practice_name: '', referral_date: '', consent_to_share: false });

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/outcomes/30day/${patient_id}/preview`);
      setData(data);
      if (data?.gp_referrer) setGp({ ...gp, ...data.gp_referrer });
    } catch (e) {
      toast.error('Could not load report data.');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [patient_id]);

  const saveGp = async () => {
    setSavingGp(true);
    try {
      await api.put(`/patients/${patient_id}/gp-referrer`, gp);
      toast.success('Referring GP saved.');
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed.'); }
    finally { setSavingGp(false); }
  };

  const downloadPdf = async () => {
    // Fetch via axios so the Bearer token travels with the request — a raw
    // window.open() is blocked by popup blockers and can miss auth.
    toast.info('Generating PDF…');
    try {
      const r = await api.get(`/outcomes/30day/${patient_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (!w) {
        const a = document.createElement('a');
        a.href = url; a.download = `outcomes-30day-${patient_id.slice(0, 8)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate the PDF.');
    }
  };

  const sendToGp = async () => {
    if (!gp.email) return toast.error('Add a GP email first.');
    if (!gp.consent_to_share) return toast.error('Patient consent is required before sending.');
    if (!await safeConfirm(`Email the 30-day outcomes report to ${gp.email}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/outcomes/30day/${patient_id}/send-to-gp`);
      if (data?.resend_configured === false) toast.info('Email service not configured.');
      else toast.success(`Sent to ${data?.to}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed.'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="p-8">Loading…</div>;
  if (!data) return <div className="p-8 text-fg-muted">No data available.</div>;

  const pain = data.pain || {};
  const adh = data.adherence || {};
  const sessions = data.sessions || {};
  const goals = data.goals || {};

  return (
    <div className="p-4 md:p-8 max-w-4xl" data-testid="outcomes-30day-page">
      <div className="mb-6">
        <Link to={`/admin/patients/${patient_id}`} className="text-xs text-fg-muted inline-flex items-center gap-1 hover:text-brand mb-2" data-testid="o30-back">
          <ChevronLeft className="w-3 h-3"/> Back to chart
        </Link>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">30-DAY OUTCOMES REPORT</div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="o30-title">
              {data.patient?.full_name || 'Patient'}
            </h1>
            <p className="text-fg-muted text-sm mt-1">
              Window {data.window?.from?.slice(0,10)} → {data.window?.to?.slice(0,10)} ({data.window_days} days).
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={downloadPdf} className="btn-secondary inline-flex items-center gap-2" data-testid="o30-pdf">
              <FileDown className="w-4 h-4"/> Preview PDF
            </button>
            <button onClick={sendToGp} disabled={sending || !gp.email || !gp.consent_to_share}
                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                    title={!gp.email ? 'Add a GP email' : !gp.consent_to_share ? 'Patient consent required' : ''}
                    data-testid="o30-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Mail className="w-4 h-4"/>}
              Send to GP
            </button>
          </div>
        </div>
      </div>

      {/* Headline metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="o30-headline">
        <Tile icon={CheckCircle2} label="Sessions attended" value={sessions.completed || 0} testid="o30-sessions"/>
        <Tile icon={TrendingDown} label="Pain delta"
              value={pain.delta != null ? `${pain.delta >= 0 ? '−' : '+'}${Math.abs(pain.delta)} pts` : '—'}
              sub={pain.pct_change != null ? `${pain.pct_change}% change` : null}
              good={pain.delta != null && pain.delta > 0} testid="o30-pain"/>
        <Tile icon={Target} label="HEP adherence"
              value={`${adh.adherence_pct || 0}%`}
              sub={`${adh.logged_sessions || 0} / ${adh.prescribed_sessions || 0}`}
              good={(adh.adherence_pct || 0) >= 60} testid="o30-adh"/>
        <Tile icon={Activity} label="Goals met"
              value={`${goals.achieved || 0} / ${goals.total || 0}`}
              good={(goals.achieved || 0) > 0} testid="o30-goals"/>
      </div>

      {/* GP referrer capture */}
      <div className="bento-card mb-4" data-testid="o30-gp-card">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-brand"/> Referring GP
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="GP name">
            <input className="input" value={gp.name || ''} onChange={e=>setGp({...gp, name: e.target.value})}
                   placeholder="Dr Smith" data-testid="o30-gp-name"/>
          </Field>
          <Field label="GP email">
            <input className="input" value={gp.email || ''} onChange={e=>setGp({...gp, email: e.target.value})}
                   placeholder="drsmith@practice.com" data-testid="o30-gp-email"/>
          </Field>
          <Field label="Practice name">
            <input className="input" value={gp.practice_name || ''} onChange={e=>setGp({...gp, practice_name: e.target.value})}
                   data-testid="o30-gp-practice"/>
          </Field>
          <Field label="Phone">
            <input className="input" value={gp.phone || ''} onChange={e=>setGp({...gp, phone: e.target.value})}
                   data-testid="o30-gp-phone"/>
          </Field>
          <Field label="Referral date">
            <DateInput value={(gp.referral_date || '').slice(0, 10)}
                   onChange={e=>setGp({...gp, referral_date: e.target.value})} data-testid="o30-gp-date"/>
          </Field>
          <label className="flex items-start gap-2 self-end" data-testid="o30-gp-consent">
            <input type="checkbox" checked={!!gp.consent_to_share}
                   onChange={e=>setGp({...gp, consent_to_share: e.target.checked})} className="mt-1"/>
            <span className="text-xs">
              <strong>Patient consents</strong> to share clinical outcomes with this GP
              (GDPR / HIPAA-compliant — required for auto-mail).
            </span>
          </label>
        </div>
        <button onClick={saveGp} disabled={savingGp}
                className="btn-primary mt-3 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="o30-gp-save">
          {savingGp ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
          Save GP details
        </button>
      </div>

      {/* ROM gains table */}
      <div className="bento-card mb-4">
        <h3 className="font-heading font-semibold mb-3">Range-of-motion gains</h3>
        {(data.rom_gains || []).length === 0 ? (
          <p className="text-sm text-fg-muted" data-testid="o30-rom-empty">No ROM measurements captured this window.</p>
        ) : (
          <table className="w-full text-sm" data-testid="o30-rom-table">
            <thead className="text-xs uppercase text-fg-muted">
              <tr><th className="text-left p-2">Joint</th><th className="text-right p-2">Initial</th><th className="text-right p-2">Latest</th><th className="text-right p-2">Δ</th></tr>
            </thead>
            <tbody>
              {data.rom_gains.map((r, i) => (
                <tr key={i} className="border-t border-soft">
                  <td className="p-2">{r.joint}</td>
                  <td className="p-2 text-right text-fg-muted">{r.initial_deg}°</td>
                  <td className="p-2 text-right font-semibold">{r.latest_deg}°</td>
                  <td className={`p-2 text-right font-bold ${r.delta_deg > 0 ? 'text-[#1B5E20]' : r.delta_deg < 0 ? 'text-[#B71C1C]' : 'text-fg-muted'}`}>
                    {r.delta_deg > 0 ? '+' : ''}{r.delta_deg}°
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Diagnosis */}
      {(data.diagnosis?.text || (data.diagnosis?.icd10_codes || []).length > 0) && (
        <div className="bento-card mb-4">
          <h3 className="font-heading font-semibold mb-3">Working diagnosis</h3>
          {data.diagnosis.icd10_codes?.length > 0 && (
            <div className="text-xs text-fg-muted mb-1">ICD-10: <span className="font-mono">{data.diagnosis.icd10_codes.join(', ')}</span></div>
          )}
          {data.diagnosis.text && <p className="text-sm whitespace-pre-wrap" data-testid="o30-dx-text">{data.diagnosis.text}</p>}
        </div>
      )}

      {(!gp.email || !gp.consent_to_share) && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-[#FFF9EC] border border-[#FFB000]/30 text-[#A86F31] text-sm" data-testid="o30-gate">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
          <div>
            {!gp.email ? 'Add a GP email above to enable sending.' :
             !gp.consent_to_share ? 'Tick the patient-consent checkbox above to enable sending.' :
             'Ready to send.'}
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, good, testid }) {
  return (
    <div className={`rounded-lg border p-3 ${good ? 'bg-[#E8F5E9] border-[#1B5E20]/40' : 'border-soft bg-bg-secondary/30'}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-fg-muted">
        <Icon className="w-3.5 h-3.5"/> {label}
      </div>
      <div className={`text-2xl font-heading font-bold mt-1 ${good ? 'text-[#1B5E20]' : 'text-fg'}`}>{value}</div>
      {sub && <div className="text-[10px] text-fg-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}
